import { addMinutes, formatDistanceToNowStrict, startOfMinute } from 'date-fns';
import { FEMA_EAS_ORG_CODES } from 'types/fema.types';
import { formatDateTimeWithOffset, getLaterDateTime, parseDateTime } from 'utils/dates/date.utils';
import {
  categoryOptions,
  certaintyOptions,
  DEFAULT_DELIVERY_CHANNELS,
  deliveryChannelOptions,
  eventCodeOptions,
  getAvailableDeliveryChannelValues,
  IPAWS_CHANNEL_EXPIRY_LIMITS_FALLBACK,
  NOT_SPECIFIED_RESPONSE_TYPE,
  responseTypeOptions,
  scopeOptions,
  severityOptions,
  statusOptions,
  urgencyOptions,
  WEA_PRIORITY_VALUES,
  WEA_RESPONSE_TYPE_VALUES,
} from '../components/IpawsAlertFields/config/Ipaws.config';
import {
  getRescAlertOutsideCoordinates,
  validateJurisdictionBoundaries,
} from '../features/AlertMapEditor/utils/alertMapEditor.utils';
import {
  getIpawsAreaFindings,
  getIpawsSameGeocodeCount,
  hasIpawsSameGeocode,
  IPAWS_LEGACY_SAME_GEOCODE_LIMIT,
} from './ipawsArea.utils';
import {
  appliesChannelGrantRules,
  getChannelAreaFindings,
  getEasOrgCodeProblem,
  getSelectedChannelGrantFindings,
} from './ipawsChannelRules.utils';
import { countIpawsCharacters, IPAWS_PUBLIC_MESSAGE_LIMITS } from './ipawsLanguage.utils';
import { getIpawsPolygonFindings, serializeIpawsPolygon } from './ipawsPolygon.utils';

export { getIpawsPolygonFindings, serializeIpawsPolygon } from './ipawsPolygon.utils';
// Keep one public Alert Creator import path for screens that need the new shape helpers.
export {
  getIpawsAreaBounds,
  getIpawsAreaFindings,
  getIpawsAreaMetrics,
  getIpawsAreaShapes,
  getUpdateAreaShapes,
} from './ipawsArea.utils';

export const ALERT_MODES = {
  RESCALERT: 'rescAlert',
  IPAWS: 'ipaws',
};

const IPAWS_SEVERITY_MAP_VALUES = {
  Extreme: 90,
  Severe: 60,
  Moderate: 30,
  Minor: 10,
  Unknown: 0,
};

const getOptionValueSet = (options) => new Set(options.map((option) => option.value));

const DEFAULT_IPAWS_CONTROLLED_VALUES = {
  status: getOptionValueSet(statusOptions),
  scope: getOptionValueSet(scopeOptions),
  category: getOptionValueSet(categoryOptions),
  eventCode: getOptionValueSet(eventCodeOptions),
  responseType: getOptionValueSet(responseTypeOptions),
  urgency: getOptionValueSet(urgencyOptions),
  severity: getOptionValueSet(severityOptions),
  certainty: getOptionValueSet(certaintyOptions),
  deliveryChannels: getOptionValueSet(deliveryChannelOptions),
};

const isBlankText = (value) => typeof value !== 'string' || value.trim().length === 0;

const hasValidPublicInformationUrl = (value) => {
  if (isBlankText(value)) return true;

  try {
    const url = new URL(value.trim());
    return (url.protocol === 'http:' || url.protocol === 'https:') && Boolean(url.hostname);
  } catch {
    return false;
  }
};

const CAP_DATE_TIME_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T([01]\d|2[0-3]):([0-5]\d):([0-5]\d)([+-])(\d{2}):([0-5]\d)$/;

const hasValidIpawsDateTime = (value) => {
  if (typeof value !== 'string') return false;

  const match = value.match(CAP_DATE_TIME_PATTERN);
  if (!match) return false;

  const [
    ,
    yearText,
    monthText,
    dayText,
    hourText,
    minuteText,
    secondText,
    sign,
    offsetHourText,
    offsetMinuteText,
  ] = match;
  const [year, month, day, hour, minute, second, offsetHour, offsetMinute] = [
    yearText,
    monthText,
    dayText,
    hourText,
    minuteText,
    secondText,
    offsetHourText,
    offsetMinuteText,
  ].map(Number);

  if (
    year === 0 ||
    month < 1 ||
    month > 12 ||
    offsetHour > 14 ||
    (offsetHour === 14 && offsetMinute !== 0) ||
    (sign === '+' && offsetHour === 0 && offsetMinute === 0)
  ) {
    return false;
  }

  const calendarDate = new Date(0);
  calendarDate.setUTCHours(0, 0, 0, 0);
  calendarDate.setUTCFullYear(year, month - 1, day);
  calendarDate.setUTCHours(hour, minute, second, 0);

  return (
    calendarDate.getUTCFullYear() === year &&
    calendarDate.getUTCMonth() === month - 1 &&
    calendarDate.getUTCDate() === day &&
    calendarDate.getUTCHours() === hour &&
    calendarDate.getUTCMinutes() === minute &&
    calendarDate.getUTCSeconds() === second
  );
};

const hasValidCapAddressesSyntax = (addresses) =>
  /^(?:"[^"\r\n]+"|[^\s"]+)(?:\s+(?:"[^"\r\n]+"|[^\s"]+))*$/.test(addresses.trim());

const addIpawsFinding = (findings, fieldPath, message) => {
  findings.push({ fieldPath, message });
};

export const getAlertMapSeverityValue = (severity) =>
  IPAWS_SEVERITY_MAP_VALUES[severity] ?? severity;

/**
 * Formats a CAP date-time while preserving the shared local-offset behavior.
 * CAP 1.2 requires UTC to use `-00:00` instead of `+00:00` or `Z`.
 */
export const formatIpawsDateTimeWithOffset = (dateTime) =>
  formatDateTimeWithOffset(dateTime).replace(/\+00:00$/, '-00:00');

/**
 * Returns the IPAWS delivery channels that were not blocked by an update's CAP parameters.
 * @param {{ valueName?: string, value?: string }[]} parameters CAP value-code parameters.
 * @returns {string[]} Selected IPAWS delivery channel values.
 */
export const getUpdateDeliveryChannels = (parameters = []) => {
  const blockedChannels = new Set(
    parameters.filter(({ valueName }) => valueName === 'BLOCKCHANNEL').map(({ value }) => value),
  );

  return deliveryChannelOptions
    .map(({ value }) => value)
    .filter((channel) => !blockedChannels.has(channel));
};

/**
 * Returns the earliest minute-resolution value that is strictly after both now and effective.
 */
export const getMinimumIpawsExpirationTime = (effective, now = new Date()) => {
  const expirationThreshold = getLaterDateTime(now, effective);
  return expirationThreshold ? addMinutes(startOfMinute(expirationThreshold), 1) : null;
};

/**
 * Returns the first expiration problem, or ''. The channel ceilings come from
 * `meta/type.dissemination.limits` (card BE-12) and fall back to the local IDG numbers:
 * EAS and NWEM 99.5 hours (FEMA 506/406), WEA 24 hours (FEMA 613). The ceilings apply only to an
 * `Actual` draft, as on the server (`appliesChannelGrantRules`).
 */
export const getIpawsExpirationError = (
  { effective, expires, deliveryChannels, status },
  now = new Date(),
  expiryLimits = IPAWS_CHANNEL_EXPIRY_LIMITS_FALLBACK,
) => {
  if (!expires) return 'Expiration time is required.';

  if (!hasValidIpawsDateTime(expires)) {
    return 'Enter an expiration time with seconds and a numeric CAP time-zone offset.';
  }

  const expirationTime = parseDateTime(expires);
  if (!expirationTime) return 'Enter a valid expiration time.';
  if (expirationTime <= now) return 'Expiration time must be in the future.';

  const effectiveTime = effective ? parseDateTime(effective) : null;
  if (effective) {
    if (effectiveTime && expirationTime <= effectiveTime) {
      return 'Expiration time must be after the effective time.';
    }
  }

  // The backend owns CAP `sent`. For the immediate-send frontend, now is the closest safe
  // baseline; a future effective time is retained for update drafts and scheduled-send handoff.
  const estimatedSendTime =
    effectiveTime && effectiveTime > now ? effectiveTime.getTime() : now.getTime();
  const channelCeilings = appliesChannelGrantRules(status)
    ? [
        ['EAS', 'EAS', expiryLimits.easExpiresHours],
        ['NWEM', 'NWEM', expiryLimits.nwemExpiresHours],
        ['CMAS', 'WEA', expiryLimits.cmasExpiresHours],
      ]
    : [];
  const exceeded = channelCeilings.find(
    ([channel, , hours]) =>
      deliveryChannels?.includes(channel) &&
      expirationTime.getTime() > estimatedSendTime + hours * 60 * 60 * 1000,
  );
  if (exceeded) {
    const [, label, hours] = exceeded;
    return `${label} expiration must be no more than ${hours} hours after the send time.`;
  }

  return '';
};

export const getIpawsDurationLabel = ({ effective, expires }) => {
  if (getIpawsExpirationError({ effective, expires })) return '';

  const expirationTime = parseDateTime(expires);
  const duration = formatDistanceToNowStrict(expirationTime, {
    addSuffix: true,
    roundingMethod: 'round',
  });

  return `Expires ${duration}.`;
};

/**
 * Returns the form problems that must be fixed before Preview can open.
 * This makes the form helpful, but the server still performs the final safety checks.
 * @param {object} draft The current IPAWS form and map values.
 * @param {object} [options] Extra facts needed for public alerts and the permitted-area check.
 * @param {boolean} [options.isPublicDissemination] Whether the alert will reach the public.
 * @param {object|null} [options.permittedArea] The authorized area the draft targets, from
 * `permittedAreaForDraft` (femaJurisdictions.utils) — every map shape must stay inside it.
 * @param {object} [options.channelRules] The channel rules from `readIpawsChannelRules`
 * (ipawsChannelRules.utils). Omitted: the caller has no server answer (a saved Global Action
 * template); the COG grant checks do not run and any one valid EAS-ORG code passes.
 * @param {Record<string, readonly { value: string }[]>} [options.controlledOptions] The allowed
 * options for each field path. A field path that it does not name uses the local option list.
 * @param {object[]} [options.authorizedJurisdictions] `jurisdictions[]` from
 * `GET /fusion/v2/fema/jurisdictions`, for the per-(channel, geocode) check (FEMA 419/519/619).
 * @returns {{ fieldPath: string, message: string }[]} Problems grouped by form field.
 */
export const getIpawsDraftFindings = (
  draft = {},
  {
    isPublicDissemination = false,
    permittedArea = null,
    controlledOptions,
    channelRules,
    authorizedJurisdictions,
  } = {},
) => {
  const findings = [];
  const addFinding = (fieldPath, message) => addIpawsFinding(findings, fieldPath, message);
  const controlledValues = controlledOptions
    ? Object.fromEntries(
        Object.entries(DEFAULT_IPAWS_CONTROLLED_VALUES).map(([fieldPath, defaultValues]) => [
          fieldPath,
          controlledOptions[fieldPath]
            ? getOptionValueSet(controlledOptions[fieldPath])
            : defaultValues,
        ]),
      )
    : DEFAULT_IPAWS_CONTROLLED_VALUES;

  if (!controlledValues.status.has(draft.status)) {
    addFinding('status', 'Select a valid CAP status.');
  } else if (isPublicDissemination && draft.status !== 'Actual') {
    addFinding('status', 'Public dissemination, including public tests, requires Actual status.');
  }

  if (!controlledValues.scope.has(draft.scope)) {
    addFinding('scope', 'Select a valid CAP scope.');
  }

  if (draft.scope === 'Restricted' && isBlankText(draft.restriction)) {
    addFinding('restriction', 'Describe the restricted distribution rule.');
  }

  if (draft.scope === 'Private' && isBlankText(draft.addresses)) {
    addFinding('addresses', 'Enter at least one private recipient.');
  } else if (draft.scope === 'Private' && !hasValidCapAddressesSyntax(draft.addresses)) {
    addFinding(
      'addresses',
      'Separate recipients with spaces and wrap any recipient containing whitespace in double quotes.',
    );
  }

  if (
    !Array.isArray(draft.category) ||
    draft.category.length === 0 ||
    draft.category.some((category) => !controlledValues.category.has(category))
  ) {
    addFinding('category', 'Select at least one valid CAP category.');
  }

  if (isBlankText(draft.event)) {
    addFinding('event', 'Enter the event type.');
  }

  if (!controlledValues.eventCode.has(draft.eventCode)) {
    addFinding('eventCode', 'Select a valid SAME event code.');
  }

  const availableDeliveryChannels = new Set(
    getAvailableDeliveryChannelValues(draft.eventCode, channelRules?.channelEventCodes),
  );
  if (!Array.isArray(draft.deliveryChannels) || draft.deliveryChannels.length === 0) {
    addFinding('deliveryChannels', 'Select at least one delivery channel.');
  } else if (
    draft.deliveryChannels.some(
      (channel) =>
        !controlledValues.deliveryChannels.has(channel) || !availableDeliveryChannels.has(channel),
    )
  ) {
    addFinding(
      'deliveryChannels',
      'Select only delivery channels supported by the chosen SAME event code.',
    );
  }
  getSelectedChannelGrantFindings(draft, channelRules).forEach((message) =>
    addFinding('deliveryChannels', message),
  );

  const parameterValues = new Map(
    (draft.parameters ?? []).map(({ valueName, value }) => [valueName, value]),
  );
  if (draft.deliveryChannels?.includes('CMAS')) {
    const short = parameterValues.get('CMAMtext');
    const long = parameterValues.get('CMAMlongtext');
    if (isBlankText(short)) addFinding('CMAMtext', 'Enter the 90-character WEA message.');
    else if (countIpawsCharacters(short) > 90)
      addFinding('CMAMtext', 'Keep the short WEA message at or below 90 characters.');
    if (isBlankText(long)) addFinding('CMAMlongtext', 'Enter the 360-character WEA message.');
    else if (countIpawsCharacters(long) > 360)
      addFinding('CMAMlongtext', 'Keep the long WEA message at or below 360 characters.');
    if (isBlankText(parameterValues.get('WEAHandling')))
      addFinding('WEAHandling', 'Select how FEMA should handle this WEA message.');
  }
  // Decision D18: EAS-ORG is the COG profile's code (`jurisdictions.easOrgCode`), never a
  // hard-coded CIV. The channel field writes it when EAS or NWEM is selected. The server checks
  // it only for an `Actual` alert (FEMA 402/502/312), so the form does the same.
  if (
    appliesChannelGrantRules(draft.status) &&
    (draft.deliveryChannels?.includes('EAS') || draft.deliveryChannels?.includes('NWEM'))
  ) {
    const draftEasOrgCode = parameterValues.get('EAS-ORG');
    const easOrgCodeProblem = channelRules ? getEasOrgCodeProblem(channelRules) : '';
    if (easOrgCodeProblem) {
      addFinding('EAS-ORG', `EAS and NWEM cannot be used: ${easOrgCodeProblem}.`);
    } else if (channelRules && draftEasOrgCode !== channelRules.easOrgCode) {
      addFinding(
        'EAS-ORG',
        `EAS-ORG must be ${channelRules.easOrgCode}, the EAS organization code in the COG profile.`,
      );
    } else if (!channelRules && !FEMA_EAS_ORG_CODES.includes(draftEasOrgCode)) {
      addFinding('EAS-ORG', 'EAS-ORG must be one of PEP, EAS, WXR or CIV for EAS or NWEM.');
    }
  }

  (draft.languages ?? []).forEach((language, index) => {
    const base = `languages.${index}`;
    if (isBlankText(language.event)) addFinding(`${base}.event`, 'Enter the Spanish event name.');
    if (isBlankText(language.areaDescription))
      addFinding(`${base}.areaDescription`, 'Enter the Spanish affected area description.');
    if (!hasValidPublicInformationUrl(language.web)) {
      addFinding(
        `${base}.web`,
        'Enter a complete Spanish public information URL beginning with http:// or https://.',
      );
    }
    if (isBlankText(language.headline))
      addFinding(`${base}.headline`, 'Enter the Spanish headline.');
    if (isBlankText(language.description))
      addFinding(`${base}.description`, 'Enter the Spanish alert description.');
    if (isBlankText(language.instruction))
      addFinding(`${base}.instruction`, 'Enter the Spanish protective action instructions.');
    if (draft.deliveryChannels?.includes('CMAS')) {
      if (isBlankText(language.weaShortText))
        addFinding(`${base}.weaShortText`, 'Enter the Spanish 90-character WEA message.');
      else if (countIpawsCharacters(language.weaShortText) > 90)
        addFinding(
          `${base}.weaShortText`,
          'Keep the Spanish short WEA message at or below 90 characters.',
        );
      if (isBlankText(language.weaLongText))
        addFinding(`${base}.weaLongText`, 'Enter the Spanish 360-character WEA message.');
      else if (countIpawsCharacters(language.weaLongText) > 360)
        addFinding(
          `${base}.weaLongText`,
          'Keep the Spanish long WEA message at or below 360 characters.',
        );
    }
    if (!language.isReviewed)
      addFinding(`${base}.isReviewed`, 'A person must review and accept the Spanish block.');
  });

  if (!Array.isArray(draft.responseType)) {
    addFinding('responseType', 'Response types must be stored as a list.');
  } else {
    if (
      draft.responseType.some(
        (responseType) =>
          responseType !== NOT_SPECIFIED_RESPONSE_TYPE &&
          !controlledValues.responseType.has(responseType),
      )
    ) {
      addFinding('responseType', 'Select only valid CAP response types.');
    }

    if (
      draft.responseType.some((responseType) =>
        ['None', NOT_SPECIFIED_RESPONSE_TYPE].includes(responseType),
      ) &&
      draft.responseType.length > 1
    ) {
      addFinding('responseType', 'None and Not Specified must be selected by themselves.');
    }

    if (
      draft.deliveryChannels?.includes('CMAS') &&
      draft.responseType.some((responseType) => !WEA_RESPONSE_TYPE_VALUES.includes(responseType))
    ) {
      addFinding(
        'responseType',
        'Select only a WEA-supported Recommended Public Action, or choose Not Specified.',
      );
    }
  }

  ['urgency', 'severity', 'certainty'].forEach((fieldPath) => {
    if (!controlledValues[fieldPath].has(draft[fieldPath])) {
      addFinding(fieldPath, `Select a valid CAP ${fieldPath}.`);
    } else if (
      draft.deliveryChannels?.includes('CMAS') &&
      !WEA_PRIORITY_VALUES[fieldPath].includes(draft[fieldPath])
    ) {
      addFinding(
        fieldPath,
        `${fieldPath[0].toUpperCase()}${fieldPath.slice(1)} must be ${WEA_PRIORITY_VALUES[
          fieldPath
        ].join(' or ')} when WEA is selected.`,
      );
    }
  });

  if (isBlankText(draft.headline)) {
    addFinding('headline', 'Enter a headline.');
  } else if (countIpawsCharacters(draft.headline) > IPAWS_PUBLIC_MESSAGE_LIMITS.headline) {
    addFinding(
      'headline',
      `Keep the headline at or below ${IPAWS_PUBLIC_MESSAGE_LIMITS.headline} characters.`,
    );
  }

  if (isBlankText(draft.description)) {
    addFinding('description', 'Enter an alert description.');
  } else if (countIpawsCharacters(draft.description) > IPAWS_PUBLIC_MESSAGE_LIMITS.description) {
    addFinding(
      'description',
      `Keep the alert description at or below ${IPAWS_PUBLIC_MESSAGE_LIMITS.description} characters.`,
    );
  }

  if (isBlankText(draft.instruction)) {
    addFinding('instruction', 'Enter protective action instructions.');
  } else if (countIpawsCharacters(draft.instruction) > IPAWS_PUBLIC_MESSAGE_LIMITS.instruction) {
    addFinding(
      'instruction',
      `Keep protective action instructions at or below ${IPAWS_PUBLIC_MESSAGE_LIMITS.instruction} characters.`,
    );
  }

  if (!hasValidPublicInformationUrl(draft.web)) {
    addFinding(
      'web',
      'Enter a complete public information URL beginning with http:// or https://.',
    );
  }

  if (draft.effective && !hasValidIpawsDateTime(draft.effective)) {
    addFinding(
      'effective',
      'Use CAP date-time format with seconds and a numeric time-zone offset.',
    );
  }

  if (draft.onset && !hasValidIpawsDateTime(draft.onset)) {
    addFinding('onset', 'Use CAP date-time format with seconds and a numeric time-zone offset.');
  }

  const expirationError = getIpawsExpirationError(draft, new Date(), channelRules?.expiryLimits);
  if (expirationError) {
    addFinding('expires', expirationError);
  } else if (!draft.hasConfirmedExpires) {
    addFinding('expires', 'Confirm that you reviewed the expiration time and duration.');
  }

  if (isBlankText(draft.areaDesc)) {
    addFinding('areaDesc', 'Describe the affected area.');
  }

  if (!hasIpawsSameGeocode(draft)) {
    addFinding('geocodes', 'Select the target county above. IPAWS needs its SAME geocode.');
  }
  getChannelAreaFindings(draft, authorizedJurisdictions).forEach((message) =>
    addFinding('geocodes', message),
  );

  if (
    (draft.deliveryChannels?.includes('EAS') || draft.deliveryChannels?.includes('NWEM')) &&
    getIpawsSameGeocodeCount(draft) > IPAWS_LEGACY_SAME_GEOCODE_LIMIT
  ) {
    addFinding(
      'geocodes',
      `Use no more than ${IPAWS_LEGACY_SAME_GEOCODE_LIMIT} SAME geocodes for EAS or NWEM.`,
    );
  }

  getIpawsAreaFindings(draft, { permittedArea }).forEach((message) =>
    addFinding('areaShapes', message),
  );

  return findings;
};

/**
 * Creates a clean IPAWS draft with matching effective and expiration times.
 * @param {Date} now The time used as the start of the draft.
 * @returns {object} A new IPAWS draft ready for the form and map editor.
 */
export const createInitialIpawsAlertDraft = (now = new Date()) => {
  const effectiveTime = parseDateTime(now) ?? new Date();
  const expirationTime = new Date(effectiveTime.getTime() + 4 * 60 * 60 * 1000);

  return {
    status: 'Test',
    messageType: 'Alert',
    references: [],
    scope: 'Public',
    restriction: '',
    addresses: '',
    deliveryChannels: [...DEFAULT_DELIVERY_CHANNELS],
    category: [],
    event: '',
    eventCode: '',
    responseType: [],
    urgency: '',
    severity: '',
    certainty: '',
    headline: '',
    description: '',
    instruction: '',
    web: '',
    contact: '',
    // <language> of the info block. IDG 8.10: WEA needs en-US, and 604 rejects any other value.
    language: 'en-US',
    // CAP <parameter> pairs — the channel parameters (EAS-ORG, CMAMtext, CMAMlongtext, WEAHandling).
    parameters: [],
    languages: [],
    effective: formatIpawsDateTimeWithOffset(effectiveTime),
    onset: '',
    expires: formatIpawsDateTimeWithOffset(expirationTime),
    hasConfirmedExpires: false,
    areaDesc: '',
    // This is the source for every polygon, square, and circle placed on the IPAWS map.
    areaShapes: [],
    resolvedJurisdiction: null,
    geocodes: [],
    city: '',
    state: '',
    searchLocation: '',
    foundLocation: '',
  };
};

export const createInitialRescAlertDraft = (timeInputMode = 'setTime') => ({
  coordinates: [],
  lastInserted: [],
  city: '',
  state: '',
  issuedTime: timeInputMode === 'setTime' ? new Date().toISOString() : '',
  expirationTime: timeInputMode === 'setTime' ? new Date().toISOString() : '',
  severity: 25,
  headline: '',
  description: '',
  type: '',
  isInternalUse: false,
  searchLocation: '',
  foundLocation: '',
  duration: 0,
  mapCenter: '',
});

export const validateRescAlertDraft = (rescAlertDraft, jurisdictions) => {
  const errors = [];

  if ((rescAlertDraft.coordinates ?? []).length < 3) {
    errors.push('Coordinates must have 3 or more points');
  }

  if (!validateJurisdictionBoundaries(rescAlertDraft.coordinates, jurisdictions)) {
    errors.push('At least one coordinate is outside your jurisdiction.');
  }
  // One line for each point outside, so the operator knows which point to move.
  getRescAlertOutsideCoordinates(rescAlertDraft.coordinates, jurisdictions).forEach(({ message }) =>
    errors.push(message),
  );

  if (!rescAlertDraft.type) {
    errors.push('Alert type cannot be blank');
  }

  if (!rescAlertDraft.headline) {
    errors.push('Headline cannot be blank');
  }

  if (!rescAlertDraft.description) {
    errors.push('Description cannot be blank');
  }

  if (!rescAlertDraft.state) {
    errors.push('State cannot be blank');
  }

  if (rescAlertDraft.expirationTime <= rescAlertDraft.issuedTime) {
    errors.push('Expiration time cannot be before or at start time');
  }

  return errors;
};

// Removes the last inserted coordinate from both the coordinates array and lastInserted array (contains the order in which user inserted coordinates)
export const removeLastCoordinateFromAlertData = (alertData) => {
  const lastCoordinate = alertData.lastInserted[alertData.lastInserted.length - 1];

  if (!lastCoordinate) return alertData;

  return {
    ...alertData,
    coordinates: (alertData.coordinates ?? []).filter(
      (coordinate) =>
        coordinate.lat !== lastCoordinate.lat || coordinate.lng !== lastCoordinate.lng,
    ),
    lastInserted: (alertData.lastInserted ?? []).slice(0, -1), // Removes the last inserted coordinate and returns the rest of the array
  };
};

export const clearCoordinatesFromAlertData = (alertData) => {
  return {
    ...alertData,
    coordinates: [],
    lastInserted: [],
  };
};
